'use client'

import { useEffect, useRef, useState } from 'react'
import posthog from 'posthog-js'

const font = "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Inter', sans-serif"

const containerStyle: React.CSSProperties = {
  maxWidth: '640px',
  margin: '0 auto',
  padding: '60px 20px 20px',
  fontFamily: font,
}

const titleStyle: React.CSSProperties = {
  fontSize: 'clamp(28px, 5vw, 40px)',
  fontWeight: 'bold',
  marginBottom: '12px',
  color: '#111827',
}

const subtitleStyle: React.CSSProperties = {
  fontSize: '15px',
  color: '#6b7280',
  marginBottom: '32px',
  lineHeight: '1.6',
}

const formRowStyle: React.CSSProperties = {
  display: 'flex',
  gap: '10px',
  flexWrap: 'wrap',
}

const inputStyle: React.CSSProperties = {
  flex: 1,
  minWidth: '220px',
  padding: '11px 14px',
  fontSize: '14px',
  border: '1px solid #b8bfc9',
  borderRadius: '8px',
  outline: 'none',
  color: '#111827',
  fontFamily: font,
  backgroundColor: '#ffffff',
  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.06)',
}

const buttonStyle: React.CSSProperties = {
  padding: '11px 22px',
  fontSize: '14px',
  fontWeight: '600',
  color: '#fff',
  backgroundColor: '#111827',
  border: 'none',
  borderRadius: '8px',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
}

const errorStyle: React.CSSProperties = {
  marginTop: '14px',
  fontSize: '13px',
  color: '#b91c1c',
  backgroundColor: '#fef2f2',
  border: '1px solid #fecaca',
  borderRadius: '8px',
  padding: '10px 14px',
}

const progressCardStyle: React.CSSProperties = {
  marginTop: '24px',
  padding: '24px',
  backgroundColor: '#fff',
  border: '1px solid #e5e7eb',
  borderRadius: '10px',
  boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
}

const spinnerStyle: React.CSSProperties = {
  border: '3px solid #e5e7eb',
  borderTop: '3px solid #2563eb',
  borderRadius: '50%',
  width: '22px',
  height: '22px',
  animation: 'spin 0.8s linear infinite',
  flexShrink: 0,
}

const progressTrackStyle: React.CSSProperties = {
  width: '100%',
  height: '6px',
  backgroundColor: '#e5e7eb',
  borderRadius: '99px',
  overflow: 'hidden',
  marginTop: '14px',
}

const resultCardStyle: React.CSSProperties = {
  marginTop: '24px',
}

const mediaStyle: React.CSSProperties = {
  width: '100%',
  borderRadius: '10px',
  border: '1px solid #e5e7eb',
  display: 'block',
  backgroundColor: '#000',
}

const mediaBlockStyle: React.CSSProperties = {
  marginBottom: '20px',
}

const downloadButtonStyle: React.CSSProperties = {
  display: 'flex',
  width: '100%',
  boxSizing: 'border-box',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '6px',
  marginTop: '10px',
  padding: '10px 20px',
  backgroundColor: '#22c55e',
  color: '#fff',
  textDecoration: 'none',
  borderRadius: '8px',
  fontWeight: '600',
  fontSize: '14px',
  boxShadow: '0 4px 14px rgba(34, 197, 94, 0.35)',
  transition: 'background-color 0.15s ease, transform 0.1s ease',
}

const TWEET_URL_PATTERN = /^https:\/\/(?:www\.)?(?:twitter|x)\.com\/[A-Za-z0-9_]{1,15}\/status\/\d+/

type JobStatus = 'downloading' | 'uploading' | 'done' | 'error'

interface TweetMediaFile {
  cdn_url: string
  filename: string
  content_type: string
  is_video: boolean
  size: number
}

interface JobState {
  status: JobStatus
  error?: string
  files?: TweetMediaFile[]
}

// Staged, not byte-accurate: gallery-dl's per-byte progress only exists as a
// terminal display callback, not a structured value the backend can forward
// without parsing its live stdout. These map the two real backend phases to
// fixed percentages so the bar still reads as continuous progress.
const STAGE_PERCENT: Record<JobStatus, number> = {
  downloading: 35,
  uploading: 75,
  done: 100,
  error: 0,
}

const STAGE_LABEL: Record<JobStatus, string> = {
  downloading: 'Fetching media from the tweet…',
  uploading: 'Uploading…',
  done: 'Ready!',
  error: 'Failed',
}

export default function DownloadTweetVideo() {
  const [tweetUrl, setTweetUrl] = useState('')
  const [jobId, setJobId] = useState<string | null>(null)
  const [job, setJob] = useState<JobState | null>(null)
  const [inputError, setInputError] = useState<string | null>(null)
  const pollRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current)
    }
  }, [])

  const pollStatus = (id: string) => {
    if (pollRef.current) clearInterval(pollRef.current)
    pollRef.current = setInterval(async () => {
      try {
        const res = await fetch(`/api/tweet/download/status/${id}`)
        const data = await res.json()
        if (!res.ok) {
          setJob({ status: 'error', error: data?.error || 'Failed to check status' })
          if (pollRef.current) clearInterval(pollRef.current)
          return
        }
        setJob(data)
        if (data.status === 'done' || data.status === 'error') {
          if (pollRef.current) clearInterval(pollRef.current)
          if (data.status === 'done') {
            posthog.capture('tweet_video_download_succeeded', { tweet_url: tweetUrl })
          } else {
            posthog.capture('tweet_video_download_failed', { tweet_url: tweetUrl, error: data.error })
          }
        }
      } catch {
        // transient network hiccup - keep polling, next tick may succeed
      }
    }, 1200)
  }

  const handleDownload = async () => {
    setInputError(null)
    const trimmed = tweetUrl.trim()
    if (!TWEET_URL_PATTERN.test(trimmed)) {
      setInputError('Please enter a valid tweet URL, e.g. https://x.com/username/status/1234567890')
      return
    }

    setJob({ status: 'downloading' })
    setJobId(null)
    posthog.capture('tweet_video_download_started', { tweet_url: trimmed })

    try {
      const res = await fetch('/api/tweet/download/start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: trimmed }),
      })
      const data = await res.json()
      if (!res.ok) {
        setJob({ status: 'error', error: data?.error || `Server returned ${res.status}` })
        return
      }
      setJobId(data.job_id)
      pollStatus(data.job_id)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to start download'
      setJob({ status: 'error', error: message })
    }
  }

  const percent = job ? STAGE_PERCENT[job.status] : 0
  const inProgress = job && (job.status === 'downloading' || job.status === 'uploading')

  return (
    <div style={containerStyle}>
      <h1 style={titleStyle}>Twitter Video, Photo &amp; GIF Downloader</h1>
      <p style={subtitleStyle}>
        Paste a tweet link and we&apos;ll pull its video, photos, or GIF at full quality, ready to preview and
        save as MP4 or image. Free, no login, no watermark.
      </p>

      <div style={formRowStyle} className="tweet-download-form-row">
        <input
          type="text"
          placeholder="https://x.com/username/status/1234567890"
          value={tweetUrl}
          onChange={(e) => setTweetUrl(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !inProgress) handleDownload() }}
          style={inputStyle}
        />
        <button onClick={handleDownload} disabled={!!inProgress} style={{ ...buttonStyle, opacity: inProgress ? 0.6 : 1 }}>
          {inProgress ? 'Working…' : 'Fetch'}
        </button>
      </div>

      {inputError && <div style={errorStyle}>{inputError}</div>}

      {job && inProgress && (
        <div style={progressCardStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={spinnerStyle} />
            <span style={{ fontSize: '14px', color: '#374151', fontWeight: 500 }}>{STAGE_LABEL[job.status]}</span>
          </div>
          <div style={progressTrackStyle}>
            <div style={{
              width: `${percent}%`,
              height: '100%',
              backgroundColor: '#2563eb',
              borderRadius: '99px',
              transition: 'width 0.4s ease',
            }} />
          </div>
        </div>
      )}

      {job && job.status === 'error' && (
        <div style={errorStyle}>{job.error || 'Something went wrong'}</div>
      )}

      {job && job.status === 'done' && job.files && jobId && (
        <div style={resultCardStyle}>
          {job.files.map((file, i) => (
            <div key={i} style={mediaBlockStyle}>
              {file.is_video ? (
                <video src={file.cdn_url} controls style={mediaStyle} />
              ) : (
                <img src={file.cdn_url} alt="" style={mediaStyle} />
              )}
              <a
                href={`/api/tweet/download/file/${jobId}?index=${i}`}
                download
                style={downloadButtonStyle}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#16a34a' }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#22c55e' }}
                onClick={() => posthog.capture('single_tweet_media_download', {
                  tweet_url: tweetUrl,
                  file_index: i,
                  file_count: job.files!.length,
                  content_type: file.content_type,
                  is_video: file.is_video,
                })}
              >
                ↓ Download{job.files!.length > 1 ? ` #${i + 1}` : ''}
              </a>
            </div>
          ))}
        </div>
      )}

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  )
}
